import { reaction } from 'mobx';
import { useEffect, useRef, useState } from 'react';

import type { IMailrailDripStep } from '@feathr/blackbox';
import { CampaignState, MailrailDripStep } from '@feathr/blackbox';

export interface IStepLock {
  id: string;
  draggable: boolean;
  removable: boolean;
}

/**
 * Debounce (ms) for syncing an in-progress step edit up to the parent campaign. Each keystroke
 * updates the local ephemeral copy immediately (re-rendering only the editor); the parent merge —
 * which re-validates every card and re-renders the whole builder — is coalesced until typing
 * pauses. Kept well under {@link DEFAULT_DEBOUNCE_WAIT} so cross-card validation and the preview
 * stay responsive once the user stops typing.
 */
const STEP_SYNC_DEBOUNCE_WAIT = 250;

/**
 * Computes the per-step reorderable/removable state for the drip builder based on the
 * campaign's lifecycle state. In `Draft` and `Scheduled` steps are freely draggable and removable;
 * in `Stopped` and `Complete` persisted steps are pinned in place while newly-added steps remain
 * draggable, and persisted steps can only be removed from the end of the sequence; in every other
 * state the order is fully locked.
 *
 * Once a drip has run, each step is triggered by an event on the email of the step before it.
 * Removing a step from the middle would re-point the next step's trigger at an earlier step whose
 * events have already happened, so contacts part-way through the removed step could never reach
 * the steps after it. Removing from the end strands no one. The backend enforces the same rule.
 *
 * Content editing (template, sender, trigger, delay, …) is NOT gated here — it is governed by
 * the campaign-level `disabled` (`!campaign.isEditable`) threaded through the editor, the
 * `campaign.isPublished` gate inside `EmailTemplateSelect` that blocks template swapping, and
 * the template's read_only field that blocks template editing.
 *
 * @param state - The campaign's current lifecycle state.
 * @param originalIds - The ids of the steps as last loaded or saved.
 * @param ids - The current ordered list of step ids.
 * @returns One state descriptor per id, index-aligned with `ids`.
 */
export function getStepLockState(
  state: CampaignState,
  originalIds: ReadonlySet<string>,
  ids: string[],
): IStepLock[] {
  const fullyEditable = [CampaignState.Draft, CampaignState.Scheduled];
  const partiallyEditable = [CampaignState.Stopped, CampaignState.Complete];
  if (fullyEditable.includes(state)) {
    return ids.map((id) => ({ id, draggable: true, removable: true }));
  }
  // Default to fully locked if the state is not included in the above lists
  if (!partiallyEditable.includes(state)) {
    return ids.map((id) => ({ id, draggable: false, removable: false }));
  }
  // Newly-added steps always follow the pre-existing ones, so only the last pre-existing step can
  // be removed without leaving a gap in the sequence that has already run.
  const lastOriginalIndex = ids.reduce(
    (last, id, index) => (originalIds.has(id) ? index : last),
    -1,
  );
  return ids.map((id, index) => {
    const isOriginal = originalIds.has(id);
    return {
      id,
      draggable: !isOriginal,
      removable: !isOriginal || index === lastOriginalIndex,
    };
  });
}

/**
 * Provides a seeded, editable copy of a single drip step that the step form binds to. The editor
 * edits the ephemeral copy live; a MobX reaction watches the copy's dirty (changed) fields and
 * forwards them to `onChange` on each edit so the coordinator can merge them into
 * `campaign.steps[index]`. The original `step` is never mutated directly.
 *
 * @param step - The step data to seed the editable copy from.
 * @param onChange - Called with the changed-fields patch (`toDirtyJS`) on each edit.
 * @returns The ephemeral model the form binds to.
 */
export function useEphemeralDripStep(
  step: IMailrailDripStep,
  onChange: (patch: Partial<IMailrailDripStep>) => void,
): MailrailDripStep {
  const [ephemeral] = useState(() => new MailrailDripStep(step));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const dispose = reaction(
      () => ephemeral.toDirtyJS(),
      (patch) => onChangeRef.current(patch as Partial<IMailrailDripStep>),
      { delay: STEP_SYNC_DEBOUNCE_WAIT },
    );
    return () => {
      dispose();
      // A delayed reaction is cancelled (not flushed) on dispose, so deliver any edit made within
      // the final debounce window — otherwise closing the card drops the last change. Skip when
      // nothing is dirty so teardown of an unedited editor doesn't trigger a parent re-render.
      const pending = ephemeral.toDirtyJS();
      if (Object.keys(pending).length > 0) {
        onChangeRef.current(pending as Partial<IMailrailDripStep>);
      }
    };
  }, [ephemeral]);

  return ephemeral;
}
